if (!customElements.get('merch-carousel')) {
    class MerchCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-merch-track]');
        if (!this.track) return;

        this.autoplay = this.hasAttribute('autoplay');
        this.interval = parseInt(this.getAttribute('interval'), 10) || 4000;
        // How long one slide takes to glide across. Previously this was the browser's own
        // smooth-scroll duration — not settable, and different in every engine (Safari and
        // Firefox noticeably slower than Chrome). We animate it ourselves instead, so the
        // merchant gets a real speed control and every browser matches.
        this.scrollMs = parseInt(this.getAttribute('scroll-ms'), 10) || 350;
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._timer = null;
        this.loop = false;
        this.loopWidth = 0;
        // Wrap-around is the default so every existing consumer (card-grid,
        // wild-collections, wild-editorial-grid) keeps behaving as before; a section
        // opts out with data-loop="false" (merch-grid's "Loop the carousel" setting,
        // off by default — bestsellers rails should come to rest on the last item).
        this.loopEnabled = this.getAttribute('data-loop') !== 'false';

        // The stylesheet sets scroll-behavior: smooth on the rail, which also animates
        // plain scrollLeft writes. Hand that back to JS: our own animation drives the
        // slide glide, the loop wrap resets instantly instead of visibly rewinding, and
        // click-drag tracks the pointer frame-for-frame. Native touch/trackpad scrolling
        // is untouched. The CSS rule stays as the no-JS fallback.
        this.track.style.scrollBehavior = 'auto';

        this._setupLoop();
        this._measure();
        window.addEventListener('resize', this._measure);

        this.prevBtn = this.querySelector('[data-merch-prev]');
        this.nextBtn = this.querySelector('[data-merch-next]');
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(1));
        // Without wrap-around the arrows have real ends to report, so keep their
        // disabled state in step with the rail position.
        if (!this.loopEnabled) {
          this.track.addEventListener('scroll', this._onScroll, { passive: true });
          this._syncNav();
        }

        // Mouse: pause on hover, resume on leave (and on focus for keyboard).
        this.addEventListener('pointerenter', (e) => { if (e.pointerType !== 'touch') this._pause(); });
        this.addEventListener('pointerleave', (e) => { if (e.pointerType !== 'touch') this._resume(); });
        this.addEventListener('focusin', () => this._pause());
        this.addEventListener('focusout', () => {
          if (!this.contains(document.activeElement)) this._resume();
        });

        // Touch: a real swipe hands control to the shopper and stops autoplay for
        // good; a plain tap only pauses briefly (there is no pointerleave when a
        // finger lifts, so a tap would otherwise stay paused).
        let startX = 0;
        this.track.addEventListener('pointerdown', (e) => {
          this._pause();
          // A touch on the rail takes it over immediately — drop any glide still running
          // so the finger isn't fighting our animation for the scroll position.
          this._cancelAnim();
          if (e.pointerType === 'touch') {
            this._touching = true;
            this._swiped = false;
            startX = e.clientX;
          }
        }, { passive: true });
        this.track.addEventListener('pointermove', (e) => {
          if (this._touching && Math.abs(e.clientX - startX) > 8) {
            this._swiped = true;
            this._userTookControl = true;
            this._pause();
          }
        }, { passive: true });
        this.track.addEventListener('pointerup', (e) => {
          if (e.pointerType !== 'touch') return;
          this._touching = false;
          if (!this._swiped) this._resumeSoon();
        }, { passive: true });
        this.track.addEventListener('pointercancel', () => { this._touching = false; }, { passive: true });

        // Mouse / pen: click-and-drag to scroll the rail (touch keeps native
        // momentum scrolling above). Dragging only ENGAGES after the pointer has
        // moved past a threshold — a plain click is never captured and never
        // suppressed, so card links keep working normally.
        let pointerDown = false;
        let dragStartX = 0;
        let dragStartScroll = 0;
        let dragPointerId = null;
        this._dragging = false;
        this._didDrag = false;
        const DRAG_THRESHOLD = 6;
        this.track.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch' || e.button !== 0) return;
          pointerDown = true;
          this._dragging = false;
          this._didDrag = false;
          dragStartX = e.clientX;
          dragStartScroll = this.track.scrollLeft;
          dragPointerId = e.pointerId;
        });
        this.track.addEventListener('pointermove', (e) => {
          if (!pointerDown) return;
          const dx = e.clientX - dragStartX;
          if (!this._dragging) {
            if (Math.abs(dx) < DRAG_THRESHOLD) return;
            // Movement past the threshold = a real drag: take control now.
            this._dragging = true;
            this._didDrag = true;
            this.track.classList.add('is-dragging');
            try { this.track.setPointerCapture(dragPointerId); } catch (_) {}
            this._pause();
          }
          this.track.scrollLeft = dragStartScroll - dx;
          e.preventDefault();
        });
        const endDrag = (e) => {
          pointerDown = false;
          if (!this._dragging) return;
          this._dragging = false;
          try { this.track.releasePointerCapture(e.pointerId); } catch (_) {}
          this.track.classList.remove('is-dragging');
          this._normalize();
          this._resumeSoon();
        };
        this.track.addEventListener('pointerup', endDrag);
        this.track.addEventListener('pointercancel', endDrag);
        // Only a real drag suppresses the trailing click (never a plain click).
        this.track.addEventListener('click', (e) => {
          if (this._didDrag) {
            e.preventDefault();
            e.stopPropagation();
            this._didDrag = false;
          }
        }, true);

        if ('onscrollend' in window) {
          this.track.addEventListener('scrollend', this._normalize, { passive: true });
        }
        document.addEventListener('visibilitychange', this._onVisibility);

        // WP-PERF: only autoplay while the rail is on (or near) screen. Running the
        // setInterval on every carousel for the whole session — including the ones
        // several screens below the fold — keeps the main thread busy and stops the
        // page ever reaching a quiet state (Lighthouse TTI / Speed Index tank on the
        // homepage, which stacks three of these). Off-screen = paused, like tab-hidden.
        //
        // How close counts as "on screen" is the difference between the two modes. The
        // default 150px pre-roll starts the rail just BEFORE it scrolls into view, so a
        // shopper arriving at it can find it already a slide or two in. data-autoplay-
        // in-view flips that to a negative margin: the rail must be properly inside the
        // viewport before it moves, so the shopper sees it start from the first product.
        // Emitted only by merch-grid, so every other consumer keeps the pre-roll.
        if ('IntersectionObserver' in window) {
          this._offscreen = true;
          this._io = new IntersectionObserver((entries) => {
            for (const e of entries) {
              this._offscreen = !e.isIntersecting;
              if (this._offscreen) this._pause();
              else this._resume();
            }
          }, { rootMargin: this.getAttribute('data-autoplay-in-view') === 'true' ? '-80px 0px' : '150px 0px' });
          this._io.observe(this);
        } else {
          this._offscreen = false;
        }

        this._start();
      }

      disconnectedCallback() {
        this._pause();
        this._cancelAnim();
        if (this.track) this.track.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._measure);
        document.removeEventListener('visibilitychange', this._onVisibility);
        if (this._io) this._io.disconnect();
      }

      // Clone the set once so the rail can wrap around seamlessly (the clone at
      // scrollLeft and the original at scrollLeft - loopWidth look identical, so
      // resetting by loopWidth is invisible). Clones are hidden from the a11y tree
      // and taken out of the tab order, but NOT inert — inert disables hit-testing,
      // which would kill :hover (and the hover action row) on the cloned cards.
      _setupLoop() {
        if (!this.loopEnabled) return; // stop-at-the-end mode: no clones, no wrap
        if (this.track.scrollWidth <= this.track.clientWidth + 4) return; // fits, no carousel
        const items = Array.from(this.track.children);
        if (items.length < 2) return;
        this._count = items.length;
        items.forEach((li) => {
          const clone = li.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          this.track.appendChild(clone);
        });
        this.loop = true;
      }

      _measure = () => {
        if (this.loop && this.track.children[this._count]) {
          this.loopWidth = this.track.children[this._count].offsetLeft - this.track.children[0].offsetLeft;
        }
        // Flag whether the rail actually overflows, so consumers (e.g. the reviews
        // section) can reveal nav arrows only when there's something to scroll.
        this.classList.toggle('is-scrollable', this.track.scrollWidth > this.track.clientWidth + 4);
        if (!this.loopEnabled) this._syncNav();
        // Centre the arrows on the card image (top of the card), not the whole height.
        const media = this.track.querySelector('.resource-card__media, .wild-reviews__media');
        if (media) this.style.setProperty('--merch-nav-top', media.offsetHeight / 2 + 'px');
      };

      _onVisibility = () => {
        if (document.hidden) this._pause();
        else this._resume();
      };

      _normalize = () => {
        if (this.loop && this.loopWidth && this.track.scrollLeft >= this.loopWidth) {
          this.track.scrollLeft -= this.loopWidth;
        }
        if (!this.loopEnabled) this._syncNav();
      };

      // Rail ends. The 4px slack absorbs sub-pixel scroll positions (a track can settle
      // at scrollWidth - clientWidth - 0.5 and never satisfy a strict comparison).
      _atStart() {
        return this.track.scrollLeft <= 4;
      }

      _atEnd() {
        return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
      }

      _onScroll = () => {
        if (this._navFrame) return;
        this._navFrame = requestAnimationFrame(() => {
          this._navFrame = null;
          this._syncNav();
        });
      };

      // Non-looping mode only: an arrow that can't move anywhere reports itself as
      // disabled instead of silently doing nothing.
      _syncNav() {
        if (this.loopEnabled || !this.track) return;
        const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
        if (this.prevBtn) this.prevBtn.disabled = !scrollable || this._atStart();
        if (this.nextBtn) this.nextBtn.disabled = !scrollable || this._atEnd();
      }

      // Glide the rail to an exact position over scrollMs. The target is always a card
      // boundary, so scroll-snap has nothing to correct when we land. Reduced-motion (and
      // a zero duration) jumps straight there.
      _animateTo(target) {
        const t = this.track;
        this._cancelAnim();
        const start = t.scrollLeft;
        const dist = target - start;
        if (!dist) return;
        if (this.reduced || this.scrollMs <= 0) {
          t.scrollLeft = target;
          return;
        }
        // The rail snaps mandatorily, so every intermediate scrollLeft we write would be
        // yanked to the nearest card and the glide would read as a jump. Suspend snapping
        // for the duration — the same thing the click-drag path does via .is-dragging —
        // and restore it on landing, where we're already exactly on a card boundary.
        t.style.scrollSnapType = 'none';
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / this.scrollMs);
          // Ease-out cubic: leaves immediately, settles softly — reads as quick without
          // looking abrupt at the end.
          t.scrollLeft = start + dist * (1 - Math.pow(1 - p, 3));
          if (p < 1) {
            this._anim = requestAnimationFrame(tick);
          } else {
            this._anim = null;
            t.scrollLeft = target;
            t.style.scrollSnapType = '';
          }
        };
        this._anim = requestAnimationFrame(tick);
      }

      // Any user scroll/drag wins over an in-flight glide. Hand snapping back so the rail
      // settles on a card wherever it was interrupted.
      _cancelAnim() {
        if (this._anim) {
          cancelAnimationFrame(this._anim);
          this._anim = null;
        }
        if (this.track) this.track.style.scrollSnapType = '';
      }

      _step() {
        const items = this.track.children;
        if (items.length < 2) return this.track.clientWidth;
        return items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
      }

      go(dir) {
        const t = this.track;
        if (this.loop && this.loopWidth) {
          // Keep within the original set before stepping so we never hit a wall.
          if (dir > 0 && t.scrollLeft >= this.loopWidth) t.scrollLeft -= this.loopWidth;
          if (dir < 0 && t.scrollLeft <= 0) t.scrollLeft += this.loopWidth;
        }
        // Clamp to the rail's own ends so a glide can't overshoot and bounce back.
        const max = Math.max(0, t.scrollWidth - t.clientWidth);
        this._animateTo(Math.max(0, Math.min(max, t.scrollLeft + dir * this._step())));
      }

      _canAutoplay() {
        return this.autoplay && !this.reduced && this.track.scrollWidth > this.track.clientWidth + 4;
      }

      _start() {
        if (this._userTookControl || this._autoplayDone || this._offscreen || !this._canAutoplay() || this._timer) return;
        this._timer = window.setInterval(() => {
          // Stop-at-the-end mode: once the last item is in view autoplay is finished for
          // the session — there's nowhere to advance to, and jumping back to the start
          // is precisely the wrap-around this mode exists to avoid.
          if (!this.loopEnabled && this._atEnd()) {
            this._autoplayDone = true;
            this._pause();
            return;
          }
          this.go(1);
        }, this.interval);
      }

      _pause() {
        if (this._timer) {
          window.clearInterval(this._timer);
          this._timer = null;
        }
      }

      _resume() {
        if (!document.hidden && !this._offscreen) this._start();
      }

      // Stop autoplay for good (e.g. the shopper opened a card's context menu).
      pauseAutoplay() {
        this._userTookControl = true;
        this._pause();
      }

      // Resume after a short beat so swipe momentum settles first.
      _resumeSoon() {
        window.clearTimeout(this._resumeTimer);
        this._resumeTimer = window.setTimeout(() => this._resume(), 800);
      }
    }
    customElements.define('merch-carousel', MerchCarousel);
  }
